import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FixedSizeList as List, ListOnScrollProps } from "react-window";
import { throttle } from "lodash";
import { ErrorBoundary } from "../../../../components/error-boundary/error-boundary";
import { CircularProgress } from "@mui/material";
import { color } from "../../../../mui-theme/palette";

type ComponentProps = {
  file: string;
  onBottomReached?: () => void;
  enabledControl?: boolean; // zoom in/out control
};

export const ReactPdfViewer: React.FC<ComponentProps> = memo(
  ({ file, onBottomReached }: ComponentProps) => {
    useEffect(() => {
      /**
       * error: Promise.withResolvers is not a function (only some android browser)
       * github issue: https://github.com/wojtekmaj/react-pdf/issues/1811#issuecomment-2212450070
       * fixing: have to load the Legacy version of PdjJS worker which is already polyfilled
       */
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "https://unpkg.com/pdfjs-dist@4.4.168/legacy/build/pdf.worker.min.mjs",
        import.meta.url,
      ).toString();
      // pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    }, []);
    const [numPages, setNumPages] = useState(null);

    const onDocumentLoadSuccess = ({ numPages }: any) => {
      setNumPages(numPages);
    };

    const pageHeight = useMemo(() => window.innerWidth * Math.sqrt(2), []);
    const scrollHandleThrottleRef = useRef(undefined as any);
    const handleScroll = useCallback(
      (e: ListOnScrollProps) => {
        if (!scrollHandleThrottleRef.current) {
          scrollHandleThrottleRef.current = throttle((e: ListOnScrollProps) => {
            const { scrollOffset } = e;
            const currentPage = Math.ceil(scrollOffset / pageHeight) + 1;
            if (!numPages || currentPage >= numPages) {
              onBottomReached?.();
            }
          }, 500);
        }
        scrollHandleThrottleRef.current(e);
      },
      [pageHeight, numPages],
    );

    const [scale, setScale] = useState(1.0); // State for zoom level
    const zoomIn = () => setScale((prevScale) => prevScale + 0.1);
    const zoomOut = () => setScale((prevScale) => Math.max(prevScale - 0.1, 0.1));

    const rowRenderer = ({ index, style }: { index: number; style: any }) => {
      return (
        <div style={style}>
          <ErrorBoundary>
            <Page
              key={+index}
              devicePixelRatio={Math.min(2, window.devicePixelRatio)}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              pageNumber={index + 1}
              width={window.innerWidth}
              scale={scale}
            />
          </ErrorBoundary>
        </div>
      );
    };
    const listMemo = useMemo(() => {
      return (
        numPages && (
          <List
            height={window.innerHeight}
            onScroll={handleScroll}
            itemCount={numPages}
            itemSize={window.innerWidth * Math.sqrt(2)}
            width={window.innerWidth}
          >
            {rowRenderer}
          </List>
        )
      );
    }, [numPages, scale]);

    const docMemo = useMemo(() => {
      return (
        <>
          <Document
            options={{
              cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
              cMapPacked: true,
              standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts`,
            }}
            file={file}
            loading={
              <div className={`h-screen w-screen flex flex-1 items-center justify-center`}>
                <CircularProgress sx={{ color: color.majorRed }} />
              </div>
            }
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {listMemo}
          </Document>
        </>
      );
    }, [file, listMemo]);

    return docMemo;
  },
);
